import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, Col, Container, Row, Table } from "reactstrap";
import { Breadcrumbs, Btn, H5, P } from "../../AbstractElements";
import RequisitesForm from "./RequisitesForm";
import SignModal from "./SignModal";
import {
  downloadDocument,
  fetchDocuments,
  fetchRequisites,
  fetchTemplates,
} from "../../helpers/documentsApi";

const formatDate = (timestamp) => {
  if (!timestamp) return "—";
  const d = new Date(timestamp * 1000);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DocumentsPage = () => {
  const [searchParams] = useSearchParams();
  const companyIdParam = searchParams.get("location_id");
  const companyId = companyIdParam ? Number(companyIdParam) : null;

  const [templates, setTemplates] = useState([]);
  const [formats, setFormats] = useState([]);
  const [keyActions, setKeyActions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [hasRequisites, setHasRequisites] = useState(false);
  const [signing, setSigning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [templateData, documentList, requisites] = await Promise.all([
        fetchTemplates(companyId),
        fetchDocuments(companyId),
        fetchRequisites(companyId),
      ]);
      setTemplates(templateData.templates || []);
      setFormats(templateData.advertising_formats || []);
      setKeyActions(templateData.key_actions || []);
      setDocuments(documentList || []);
      setHasRequisites(Boolean(requisites.values));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSigned = async (doc) => {
    setSigning(null);
    setNotice(`Документ «${doc.title}» подписан. Номер ${doc.number}.`);
    await load();
  };

  const handleDownload = async (doc) => {
    setError("");
    try {
      await downloadDocument(doc.id, `${doc.number || doc.id}.pdf`);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Документы" title="Документы" parent="Платформа" />
      <Container fluid={true}>
        {error && <P className="text-danger">{error}</P>}
        {notice && <P className="text-success">{notice}</P>}

        <Row>
          <Col xl={7}>
            <Card>
              <CardHeader className="pb-0">
                <H5>Договоры</H5>
                <P className="text-muted mb-0">
                  Подписываются здесь же: заполняете реквизиты, читаете документ и
                  нажимаете «Подписать». Печатать и присылать скан не нужно.
                </P>
              </CardHeader>
              <CardBody>
                {loading && <P>Загрузка…</P>}
                {!loading && templates.length === 0 && (
                  <P className="text-muted mb-0">Доступных документов нет.</P>
                )}
                {!loading &&
                  templates.map((template) => (
                    <div
                      key={template.code}
                      className="d-flex justify-content-between align-items-start border-bottom py-3"
                    >
                      <div className="pe-3">
                        <div className="fw-bold">{template.title}</div>
                        <div className="text-muted">
                          {template.role_name}
                          {template.is_annex ? " · приложение" : ""}
                          {template.signed_count > 0
                            ? ` · подписано: ${template.signed_count}`
                            : ""}
                        </div>
                        {!template.parent_signed && (
                          <div className="text-muted">
                            Сначала подпишите основной договор.
                          </div>
                        )}
                        {template.blockers && template.blockers.length > 0 && (
                          <div className="text-danger">
                            Подписание недоступно: не заполнено —{" "}
                            {template.blockers.join(", ")}.
                          </div>
                        )}
                      </div>
                      <div className="text-nowrap">
                        {template.is_signed && !template.is_annex ? (
                          <Badge color="success">Подписан</Badge>
                        ) : (
                          <Btn
                            attrBtn={{
                              color: "primary",
                              disabled: !template.can_sign || !hasRequisites,
                              onClick: () => setSigning(template),
                            }}
                          >
                            Подписать
                          </Btn>
                        )}
                      </div>
                    </div>
                  ))}
                {!loading && !hasRequisites && (
                  <P className="text-muted mt-3 mb-0">
                    Чтобы подписывать документы, заполните реквизиты справа.
                  </P>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="pb-0">
                <H5>История документов</H5>
              </CardHeader>
              <CardBody>
                {documents.length === 0 && !loading && (
                  <P className="text-muted mb-0">Подписанных документов пока нет.</P>
                )}
                {documents.length > 0 && (
                  <div className="table-responsive">
                    <Table>
                      <thead>
                        <tr>
                          <th>Номер</th>
                          <th>Документ</th>
                          <th>Подписан</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc) => (
                          <tr key={doc.id}>
                            <td>{doc.number || "—"}</td>
                            <td>{doc.title}</td>
                            <td>{formatDate(doc.signed_at)}</td>
                            <td className="text-end">
                              <Btn
                                attrBtn={{
                                  color: "light",
                                  onClick: () => handleDownload(doc),
                                }}
                              >
                                Скачать PDF
                              </Btn>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl={5}>
            <RequisitesForm
              companyId={companyId}
              onSaved={() => {
                setHasRequisites(true);
                load();
              }}
            />
          </Col>
        </Row>
      </Container>

      {signing && (
        <SignModal
          template={signing}
          formats={formats}
          keyActions={keyActions}
          companyId={companyId}
          onClose={() => setSigning(null)}
          onSigned={handleSigned}
        />
      )}
    </Fragment>
  );
};

export default DocumentsPage;
